/*
 * Copyright (c) 2026 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from "react";


// CSS styles for modal
const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    width: '500px',
    maxHeight: '90%',
    overflowY: 'auto',
    position: 'relative',
    textShadow: 'none',
    color: 'black',
    fontFamily: 'arial',
    textAlign: 'left',
    fontSize: 'x-small'
};


interface TermsAndConditionsProps {
    onClose: () => void
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({onClose}) => {
    return (
      <div style={modalOverlayStyle}>
        <div style={modalStyle}>
            <h2>Terms and Conditions</h2>
            Effective Date: December 30th, 2025<br/>
            Welcome to Monster Forge by Green God Games, LLC.
            By accessing or using our website and services, you agree to comply with and be bound by these Terms and Conditions.
            Please read them carefully.
            <ol>
                <li>
                    <b>Acceptance of Terms</b><br/>
                    By creating an account and using Monster Forge, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions, as well as our Privacy Policy.
                    If you do not agree to these terms, you may not use our services.
                </li>
                <li>
                    <b>Age Requirement</b><br/>
                    You must be at least 13 years old to create an account and use Monster Forge.
                    By creating an account, you represent and warrant that you meet this age requirement.
                    We do not knowingly collect information from or allow children under 13 to use our services.
                    If you are under 13, you are not permitted to use this service.
                </li>
                <li>
                    <b>Account Responsibilities</b><br/>
                    You are responsible for:
                    <ul>
                        <li>Maintaining the confidentiality of your account credentials.</li>
                        <li>All activities that occur under your account.</li>
                        <li>Notifying us immediately of any unauthorized use of your account.</li>
                    </ul>
                    Green God Games, LLC is not liable for any loss or damage arising from your failure to protect your account information.
                </li>
                <li>
                    <b>Data Value and Backup</b><br/>
                    While we strive to provide reliable service and data storage, you acknowledge and agree that:
                    <ul>
                        <li>All content you create and store (including monsters, characters, and other data) has <b>no monetary value</b>.</li>
                        <li>We are not liable for any loss, corruption, or deletion of your data for any reason, including but not limited to technical failures, service interruptions, or account termination.</li>
                        <li>You are solely responsible for maintaining your own backups of any content you create.</li>
                        <li>We provide data storage as a convenience and make no guarantees regarding data retention or availability.</li>
                    </ul>
                </li>
                <li>
                    <b>Prohibited Content and Conduct</b><br/>
                    You agree not to create, upload, share, or make public any content that:
                    <ul>
                        <li>Is pornographic, sexually explicit, or adult-oriented in nature.</li>
                        <li>Is offensive, hateful, discriminatory, or promotes violence or illegal activities.</li>
                        <li>Infringes on the intellectual property rights of others.</li>
                        <li>Contains malware, viruses, or any harmful code.</li>
                        <li>Violates any applicable local, state, national, or international law.</li>
                    </ul>
                    We reserve the right to remove any content that violates these terms and to suspend or terminate accounts of users who repeatedly violate these policies.
                </li>
                <li>
                    <b>Content Sharing and Public Content</b><br/>
                    When sharing features are implemented:
                    <ul>
                        <li>You are responsible for ensuring that any content you share with other users or make public complies with these Terms and Conditions.</li>
                        <li>By making content public or sharing it with others, you grant Green God Games, LLC a non-exclusive, worldwide, royalty-free license to display, distribute, and use that content solely for the purpose of operating and improving the service.</li>
                        <li>You retain ownership of all content you create, but you are responsible for ensuring you have the right to share it.</li>
                        <li>We reserve the right to remove any publicly shared content that violates these terms.</li>
                    </ul>
                </li>
                <li>
                    <b>Service Modifications and Availability</b><br/>
                    Green God Games, LLC reserves the right to:
                    <ul>
                        <li>Modify, suspend, or discontinue any aspect of the service at any time without notice.</li>
                        <li>Change subscription pricing, features, or terms with reasonable notice to active subscribers.</li>
                        <li>Perform maintenance that may temporarily interrupt service availability.</li>
                    </ul>
                    We are not liable for any modification, suspension, or discontinuation of the service.
                </li>
                <li>
                    <b>Subscription and Billing</b><br/>
                    If you subscribe to a paid plan:
                    <ul>
                        <li>You agree to pay all fees associated with your chosen subscription tier.</li>
                        <li>Subscriptions automatically renew unless canceled before the renewal date.</li>
                        <li>We do not provide refunds for partial subscription periods.</li>
                        <li>You can cancel your subscription at any time, and it will remain active until the end of the current billing period.</li>
                    </ul>
                </li>
                <li>
                    <b>Intellectual Property</b><br/>
                    <ul>
                        <li>Monster Forge and all associated materials, including but not limited to software, design, graphics, and documentation, are the property of Green God Games, LLC and are protected by copyright and other intellectual property laws.</li>
                        <li>You may not copy, modify, distribute, sell, or reverse engineer any part of our service.</li>
                        <li>Content you create using Monster Forge remains your property, but you must ensure you have the rights to any third-party content you incorporate (such as images).</li>
                    </ul>
                </li>
                <li>
                    <b>Limitation of Liability</b><br/>
                    To the fullest extent permitted by law:
                    <ul>
                        <li>Monster Forge is provided "as is" without warranties of any kind, either express or implied.</li>
                        <li>Green God Games, LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the service.</li>
                        <li>Our total liability for any claims related to the service shall not exceed the amount you paid for the service in the 12 months preceding the claim.</li>
                    </ul>
                </li>
                <li>
                    <b>Account Termination</b><br/>
                    We reserve the right to suspend or terminate your account at any time, with or without cause, including but not limited to:
                    <ul>
                        <li>Violation of these Terms and Conditions.</li>
                        <li>Fraudulent, abusive, or illegal activity.</li>
                        <li>Extended periods of inactivity (with reasonable notice).</li>
                    </ul>
                    Upon termination, you will lose access to your account and all associated data.
                </li>
                <li>
                    <b>Governing Law</b><br/>
                    These Terms and Conditions shall be governed by and construed in accordance with the laws of the United States and the state in which Green God Games, LLC is registered, without regard to conflict of law principles.
                </li>
                <li>
                    <b>Changes to These Terms</b><br/>
                    We may update these Terms and Conditions from time to time to reflect changes to our services or for legal or regulatory reasons.
                    We will notify users of significant changes through email or prominent notice on our website.
                    Continued use of the service after changes are posted constitutes acceptance of the updated terms.
                </li>
                <li>
                    <b>Contact Us</b><br/>
                    If you have any questions about these Terms and Conditions, please contact us at:<br/>
                    Email: <a href="mailto:support@GreenGodGames.com" target="_blank" rel="noreferrer">support@GreenGodGames.com</a>
                </li>
            </ol>
            <br/>
            <div style={{textAlign: 'right'}}>
                <button onClick={() => onClose()}>Close</button>
            </div>
        </div>
     </div>
    );
  };

  export default TermsAndConditions;
